/**
 * Pure utility functions for spine-snapshot serialization / deserialization,
 * timeline-filter normalization, and doc-caption derivation.
 *
 * Extracted from VariantCShell.tsx (Phase 0 refactor) — no React imports,
 * no side effects. Safe to call from any context.
 */

import type { CustomSpineSection, DocTypeConfig, DocumentSpineSnapshotSection } from "../../api/client";
import type { LoomFreshSelection } from "../components/LoomFresh";
import type { SpineSection } from "../hooks/useVariantCRun";
import type { TimelineFilter, VariantCState } from "../state";
import { orderSpineSections } from "./spineOrder";

// ---------------------------------------------------------------------------
// Project naming
// ---------------------------------------------------------------------------

/** Synthesize a project_name for the backend's startRun call.
 *  Variant C doesn't surface this concept to the writer — it's just
 *  an internal id the backend uses to namespace artefacts on disk. */
export function synthesizeProjectName(selection: LoomFreshSelection): string {
  const stamp = new Date().toISOString().replace(/[:.]/g, "").slice(0, 15);
  // <docCode>-<libIdShort>-<isoStamp>
  return `vc-${selection.docCode}-${selection.libraryId.slice(0, 6)}-${stamp}`;
}

// ---------------------------------------------------------------------------
// Spine sections from config
// ---------------------------------------------------------------------------

/** Build the spine's initial section list from a doc-type config.
 *
 *  CRITICAL: spine rows are MODULES (e.g. "CER-02"), not skeleton
 *  sections (e.g. "2", "2.1"). The agent works in modules — that's
 *  what SSE ``module_start`` / ``section_ready`` events carry, and
 *  what ``getSectionContent(runId, moduleId)`` expects. Using
 *  skeleton section numbers as spine ids was the root cause of
 *  "no action happening" + "Module 2 not found" symptoms.
 *
 *  Skeleton stays for Word document scaffolding (separate concern,
 *  handled in handleProgressEvent's gate_start branch).
 *
 *  Ordering: DOCUMENT order (``orderSpineSections`` on the display
 *  number), not gate order. Gates are execution order — for CSR the
 *  Finalize gate carries ``2, 13, 14, 15, 16`` so §2 Synopsis used to
 *  trail §12.6. Gates are still walked (they define WHICH modules
 *  exist); the resulting rows are then sorted so the spine reads like
 *  the document's table of contents.
 *
 *  Display:
 *    - ``id``     = module_id (e.g. "CER-02")  — backend identity
 *    - ``number`` = first skeleton section in the module (e.g. "2")
 *                   — what writers see as "section 2" in the gutter
 *    - ``title``  = module title (e.g. "Scope of the Clinical
 *                   Evaluation")
 */
export function spineSectionsFromConfig(config: DocTypeConfig): SpineSection[] {
  const modules = config.modules ?? {};
  const gates = Array.isArray(config.gates) ? config.gates : [];

  if (gates.length > 0) {
    const seen = new Set<string>();
    const rows: SpineSection[] = [];
    for (const gate of gates) {
      const moduleIds = (gate.modules ?? []) as string[];
      for (const mid of moduleIds) {
        if (seen.has(mid)) continue;
        seen.add(mid);
        const meta = modules[mid] ?? { sections: [], title: mid };
        const firstSection = (meta.sections ?? [])[0] ?? mid;
        rows.push({
          id: mid,
          number: firstSection,
          title: meta.title ?? mid,
          status: "queued",
        });
      }
    }
    if (rows.length > 0) return orderSpineSections(rows);
  }

  // Fallback: doc type with no gates declared. Modules come from the
  // dict in YAML-parser order, which is arbitrary — document-order
  // sort makes the result deterministic.
  return orderSpineSections(
    Object.entries(modules).map(([id, m]) => ({
      id,
      number: (m.sections ?? [])[0] ?? id,
      title: m.title ?? id,
      status: "queued" as const,
    })),
  );
}

// ---------------------------------------------------------------------------
// Spine sections from a custom (existing-doc) outline
// ---------------------------------------------------------------------------

/** Build the spine for a CUSTOM submission from the Word doc's heading outline.
 *
 *  Unlike ``spineSectionsFromConfig`` — which derives rows from the doctype
 *  template and sorts by section number — a custom spine mirrors the writer's
 *  existing document exactly:
 *    - ``id``     = the synthetic module id from the outline ("CUSTOM-1"…),
 *                   the identity regenerate / audit route on.
 *    - ``number`` = the heading's own number (blank if it had none).
 *    - ``title``  = the heading text.
 *    - ``status`` = ``"drafted"`` (green dot) when the heading — or any
 *                   descendant heading — has prose, else ``"queued"`` (empty).
 *
 *  DOCUMENT ORDER IS PRESERVED AS READ. We deliberately do NOT call
 *  ``orderSpineSections``: custom headings often carry no number (or a
 *  non-numeric one), so number-sorting would scramble the writer's document.
 */
export function spineSectionsFromOutline(outline: readonly CustomSpineSection[]): SpineSection[] {
  return outline.map((node) => ({
    id: node.id,
    number: node.number ?? "",
    title: node.title,
    status: node.has_content ? ("drafted" as const) : ("queued" as const),
  }));
}

// ---------------------------------------------------------------------------
// Hash / filter helpers
// ---------------------------------------------------------------------------

/**
 * Short-form of a sha256:<hex> hash for human-facing labels on the
 * Timeline's library-swap events. Takes the first 7 hex chars after
 * the 'sha256:' prefix (convention matches git short-ref length).
 * Returns the raw string unchanged if the prefix isn't present.
 */
export function shortHash(full: string): string {
  const prefix = "sha256:";
  if (!full.startsWith(prefix)) return full;
  return full.slice(prefix.length, prefix.length + 7);
}

/**
 * True when every dimension of ``filter`` is null (no narrowing).
 * Kept as a helper (not a state.ts export) because it's only
 * interesting at the render layer — the reducer treats empty filter
 * as "just a filter that matches all", not a special state.
 */
export function isEmptyFilter(filter: TimelineFilter): boolean {
  return filter.sectionId === null && filter.kind === null && filter.actor === null && filter.sinceMs === null;
}

// ---------------------------------------------------------------------------
// Spine-snapshot serialization
// ---------------------------------------------------------------------------

export function isValidPersistedSpineSnapshotSection(section: DocumentSpineSnapshotSection): boolean {
  // ``number`` is intentionally NOT required: a custom-submission heading read
  // from an existing Word doc often has no section number (blank gutter), and
  // dropping those rows on restore would erase them from the spine.
  return !!section.id && !!section.title && !!section.status;
}

export function buildPersistedSpineSnapshotSection(section: SpineSection): DocumentSpineSnapshotSection {
  const snapshot: DocumentSpineSnapshotSection = {
    id: section.id,
    number: section.number,
    title: section.title,
    status: section.status,
  };
  if (section.subline != null) snapshot.subline = section.subline;
  if (section.sublineStartedAt != null) {
    snapshot.sublineStartedAt = section.sublineStartedAt;
  }
  if (section.citationCount != null) snapshot.citationCount = section.citationCount;
  if (section.retrievalProgress !== undefined) {
    snapshot.retrievalProgress = section.retrievalProgress;
  }
  if (section.requirementCount != null) {
    snapshot.requirementCount = section.requirementCount;
  }
  if (section.wordCount != null) snapshot.wordCount = section.wordCount;
  if (section.quality != null) snapshot.quality = section.quality;
  if (section.flaggedCount != null) snapshot.flaggedCount = section.flaggedCount;
  if (section.contentGapCount != null) snapshot.contentGapCount = section.contentGapCount;
  if (section.regulatorFlagCount != null) snapshot.regulatorFlagCount = section.regulatorFlagCount;
  if (section.acceptedAt != null) snapshot.acceptedAt = section.acceptedAt;
  if (section.matchedModuleId !== undefined) snapshot.matchedModuleId = section.matchedModuleId;
  if (section.matchedTitle !== undefined) snapshot.matchedTitle = section.matchedTitle;
  if (section.matchResolved != null) snapshot.matchResolved = section.matchResolved;
  if (section.instruction != null) snapshot.instruction = section.instruction;
  return snapshot;
}

export function restorePersistedSpineSnapshotSection(section: DocumentSpineSnapshotSection): SpineSection | null {
  if (!isValidPersistedSpineSnapshotSection(section)) return null;

  const restored: SpineSection = {
    id: section.id,
    number: section.number,
    title: section.title,
    status: section.status as SpineSection["status"],
  };
  if (section.subline != null) restored.subline = section.subline;
  if (section.sublineStartedAt != null) {
    restored.sublineStartedAt = section.sublineStartedAt;
  }
  if (section.citationCount != null) restored.citationCount = section.citationCount;
  if (section.retrievalProgress !== undefined) {
    restored.retrievalProgress = section.retrievalProgress ?? null;
  }
  if (section.requirementCount != null) {
    restored.requirementCount = section.requirementCount;
  }
  if (section.wordCount != null) restored.wordCount = section.wordCount;
  if (section.quality != null) {
    restored.quality = section.quality as SpineSection["quality"];
  }
  if (section.flaggedCount != null) restored.flaggedCount = section.flaggedCount;
  if (section.contentGapCount != null) restored.contentGapCount = section.contentGapCount;
  if (section.regulatorFlagCount != null) restored.regulatorFlagCount = section.regulatorFlagCount;
  if (section.acceptedAt != null) restored.acceptedAt = section.acceptedAt;
  if (section.matchedModuleId !== undefined) restored.matchedModuleId = section.matchedModuleId;
  if (section.matchedTitle !== undefined) restored.matchedTitle = section.matchedTitle;
  if (section.matchResolved != null) restored.matchResolved = section.matchResolved;
  if (section.instruction != null) restored.instruction = section.instruction;
  return restored;
}

export function toPersistedSpineSnapshot(sections: readonly SpineSection[]): DocumentSpineSnapshotSection[] {
  return sections.map(buildPersistedSpineSnapshotSection);
}

export function normalisePersistedSpineSnapshot(
  snapshot: DocumentSpineSnapshotSection[] | null | undefined,
): SpineSection[] | null {
  if (!Array.isArray(snapshot) || snapshot.length === 0) return null;
  const normalised = snapshot.flatMap((section) => {
    const restored = restorePersistedSpineSnapshotSection(section);
    return restored ? [restored] : [];
  });
  // Snapshots persisted before the spine sorted by document order carry
  // gate order — re-sort on restore so old sessions heal on load.
  return normalised.length > 0 ? orderSpineSections(normalised) : null;
}

// ---------------------------------------------------------------------------
// Timeline filter normalization
// ---------------------------------------------------------------------------

/**
 * Restore a persisted ``timeline_filter`` blob from MongoDB into a
 * concrete ``TimelineFilter``. Returns null when the value is missing,
 * not an object, or has no non-null dimension (i.e. equivalent to the
 * empty filter — no point dispatching for a no-op).
 *
 * Defensive: bad shapes are coerced to ``null`` for that dimension
 * rather than dropped wholesale, so a partially-corrupted persisted
 * filter still restores the salvageable parts.
 */
export function normalisePersistedTimelineFilter(
  raw: Record<string, unknown> | null | undefined,
): TimelineFilter | null {
  if (!raw || typeof raw !== "object") return null;
  const sectionId = typeof raw.sectionId === "string" ? raw.sectionId : null;
  const kindRaw = raw.kind;
  const kind = typeof kindRaw === "string" && kindRaw.length > 0 ? (kindRaw as TimelineFilter["kind"]) : null;
  const actorRaw = raw.actor;
  const actor = actorRaw === "asthra" || actorRaw === "you" ? actorRaw : null;
  const sinceMs = typeof raw.sinceMs === "number" && Number.isFinite(raw.sinceMs) ? raw.sinceMs : null;
  if (!sectionId && !kind && !actor && sinceMs === null) return null;
  return { sectionId, kind, actor, sinceMs };
}

// ---------------------------------------------------------------------------
// Doc caption derivation
// ---------------------------------------------------------------------------

export function deriveDocCaption(
  _state: VariantCState,
  activeSelection: LoomFreshSelection | null,
): { title: string; caption: string } {
  // Show library/doc the moment we have a selection — this covers
  // plan-review (reducer still "idle", no runId yet), "starting",
  // and the full run lifecycle. Earlier logic gated on runId alone,
  // which left the header stuck on "Pick a library and doc to
  // begin" throughout the pre-run plan-review surface even though
  // the writer had already picked both. The idle copy is now
  // reserved for the true idle state — no selection in hand.
  if (activeSelection) {
    return {
      title: activeSelection.libraryName,
      caption: `${activeSelection.docCode}${activeSelection.docFramework ? ` · ${activeSelection.docFramework}` : ""}`,
    };
  }
  return { title: "New draft", caption: "Pick a library and doc to begin" };
}
